import * as React from 'react';
import { LocationsContext } from 'core/components/LocationsProvider';
import { LocationWithCoordinates } from 'lib/types';
import { getUserTimezone } from '@bondvet/web-app-lib/util/timezone';
import moment from 'lib/momentTimezone';
import { DayOfTheWeek } from '@bondvet/types/common';

function getIsClosed(
    locations: ReadonlyArray<LocationWithCoordinates>,
    locationIds: ReadonlyArray<string>,
    isToday: boolean,
    date?: string,
): boolean {
    if (locations.length > 0 && locationIds.length > 0) {
        const selectedLocations = locations.filter((location) =>
            locationIds.includes(location._id),
        );
        const now = new Date();

        return selectedLocations.reduce<boolean>(
            (result, { timezone: locationTimeZone, locationHours }) => {
                if (!result) {
                    // already found a location that is still open
                    return false;
                }

                if (locationHours) {
                    const timezone = locationTimeZone || getUserTimezone();
                    const dayOfTheWeek = moment(date)
                        .tz(timezone)
                        .format('dddd')
                        .toLocaleLowerCase() as DayOfTheWeek;
                    const todaysOpeningHours = locationHours[dayOfTheWeek];

                    if (todaysOpeningHours) {
                        const startTime = moment.tz(
                            todaysOpeningHours[0].start,
                            'HH:mm',
                            timezone,
                        );
                        const endTime = moment.tz(
                            todaysOpeningHours[0].end,
                            'HH:mm',
                            timezone,
                        );
                        // then we check if isToday and if right now is before the end time
                        if (
                            isToday &&
                            moment.tz(endTime, timezone).isAfter(now)
                        ) {
                            return false;
                        }

                        const duration = moment.duration(
                            endTime.diff(startTime),
                        );
                        const hours = duration.asHours();

                        if (!isToday && hours > 0.5) {
                            return false;
                        }
                    }
                }

                return true;
            },
            true,
        );
    }

    return true;
}

export default function useIsClosed(
    locationIds: ReadonlyArray<string>,
    isToday: boolean,
    date?: string,
    customLocations?: LocationWithCoordinates[],
): boolean {
    const { locations } = React.useContext(LocationsContext);
    const [isClosed, setIsClosed] = React.useState(() =>
        getIsClosed(locations, locationIds, isToday, date),
    );

    React.useEffect(() => {
        setIsClosed(
            getIsClosed(
                customLocations ?? locations,
                locationIds,
                isToday,
                date,
            ),
        );
    }, [customLocations, date, isToday, locationIds, locations]);
    return isClosed;
}
